import { useState, useEffect, useMemo } from "react";
import { Theme, useTheme } from "@mui/material";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import AccessAlarmOutlinedIcon from "@mui/icons-material/AccessAlarmOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import firebaseService from "../firebase_service/firebaseService";
import { NavLink } from "react-router-dom";
import { FaArrowDown, FaArrowUp } from "react-icons/fa";
import { Kpi } from "../Types";
import { useMonthlyRegisteredDevices } from "../components/hooks/useRegisteredPerFWPerMonth";
import StudioUsersWidget from "./Dashboard/components/StudioUsersWidget";
import { useNumberOfAuthorsCreatedKlydos } from "../components/hooks/useNumberOfAuthorsCreatedKlydos";
import { useNumberOfPendingPublicationRequests } from "../components/hooks/useNumberOfPendingPublicationRequests";
import { ReviewTab } from "./Reviews/components/Reviews";
import { useNumberOfCollectionsInReview } from "../components/hooks/useNumberOfCollectionsInReview";
import useUnregisteredDevicesCount from "../components/hooks/useUnregisteredDevicesCount";
import useOpenedSwissClocksCount from "../components/hooks/useOpenedSwissClocksCount";
import useKlydoStats from "../components/hooks/useKlydoStats";
import useClockSettingsDistribution from "../components/hooks/useClockSettingsDistribution";
import MenuNavigationWidget from "./Dashboard/components/MenuNavigationWidget";
import HeartLongPressWidget from "./Dashboard/components/HeartLongPressWidget";
import useCollectionAnalytics from "../components/hooks/useCollectionAnalytics";
import { useArtistsClocksCount } from "../components/hooks/useArtistsClocksCount";
import useExploreClocksCount from "../components/hooks/useExploreClocksCount";
import { resizeCloudinary, isVideo } from "../utils/util";
import klydoErrorIcon from "../assets/img/klydo_error.png";
import UnregisteredDevicesListModal from "./Dashboard/components/UnregisteredDevicesListModal";
import RemindersWidget from "./Dashboard/components/RemindersWidget";
import ReminderKlydosListWidget from "./Dashboard/components/ReminderKlydosListWidget";
import DailyRhythmCollectionWidget from "./Dashboard/components/DailyRhythmCollectionWidget";
import AvgUniqueKlydosWidget from "./Dashboard/components/AvgUniqueKlydosWidget";
import NewClockOSFeaturesCard from "./Dashboard/components/NewClockOSFeaturesCard";
import KlydoFeedAnalyticsCard from "./Dashboard/components/KlydoFeedAnalyticsCard";
import AddCollectionQrCard from "./Dashboard/components/AddCollectionQrCard";
import UpfrontPaymentsWidget from "./Dashboard/components/UpfrontPaymentsWidget";
import AffiliatePaymentsWidget from "./Dashboard/components/AffiliatePaymentsWidget";
import KlydoGreetOrdersWidget from "./Dashboard/components/KlydoGreetOrdersWidget";
import ExploreFavoritesCard from "./Dashboard/components/ExploreFavoritesCard";
import AiGreetAnalyticsWidget from "./Dashboard/components/AiGreetAnalyticsWidget";
import CollectionProductPageEngagementWidget from "./Dashboard/components/CollectionProductPageEngagementWidget";
import ArtistPreviewedKlydoWidget from "./Dashboard/components/ArtistPreviewedKlydoWidget";
import SentToClockWidget from "./Dashboard/components/SentToClockWidget";
import EcoModeWidget from "./Dashboard/components/EcoModeWidget";
import useCollabpayWidgetData from "../components/hooks/useCollabpayWidgetData";
import useDashboardOrders from "../components/hooks/useDashboardOrders";
import useCollectionPurchaseStats from "../components/hooks/useCollectionPurchaseStats";
import { CollectionPurchaseStatsResponse } from "../api/admin";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { useArtistsLandingPageStats } from "../components/hooks/useArtistsLandingPageStats";
import PageHeader from "components/PageHeader";
import DSBox from "views/system-design/DSBox";
import DSStack from "views/system-design/DSStack";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSTypography from "views/system-design/DSTypography";
import DSSelect from "views/system-design/DSSelect";
import DSMenuItem from "views/system-design/DSMenuItem";
import DSTextField from "views/system-design/DSTextField";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import {
  DSTable,
  DSTableBody,
  DSTableCell,
  DSTableHead,
  DSTableRow,
} from "views/system-design/Table";
import { useNumberOfCollectionsInMarketplace } from "../components/hooks/useNumberOfCollectionsInMarketplace";

// ---------------------------------------------------------------------------
// Layout primitives
// ---------------------------------------------------------------------------

/** Flat studio card used by all regular (non-hero) dashboard cards. */
const flatCardSx = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "surface.card",
  backgroundImage: "none",
  borderRadius: "20px",
} as const;

/** Gradient statement card used by the hero band. */
const heroCardSx = (theme: Theme) =>
  ({
    position: "relative",
    overflow: "hidden",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: theme.palette.surface.cardGradient,
    borderRadius: "20px",
  }) as const;

const cardFooterSx = (theme: Theme) =>
  ({
    borderTop: `1px solid ${theme.palette.divider}`,
    px: 2,
    py: 1,
    width: "100%",
    fontSize: 12,
    color: "text.secondary",
  }) as const;

/** Responsive grid used inside each section. */
const sectionGridSx = {
  display: "grid",
  gap: 2,
  alignItems: "stretch",
  gridTemplateColumns: {
    xxs: "1fr",
    sm: "repeat(2, 1fr)",
    xl: "repeat(3, 1fr)",
  },
} as const;

/** Wide chart/table cards span two columns once the grid has them. */
const wideSpanSx = { gridColumn: { xxs: "auto", sm: "span 2" } } as const;

/** Full-bleed cards span the entire section row. */
const fullSpanSx = { gridColumn: { xxs: "auto", sm: "1 / -1" } } as const;

const SectionHeader = ({ title }: { title: string }) => (
  <DSBox sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
    <DSTypography
      sx={{
        fontFamily: "Gilroy-SemiBold",
        fontSize: 13,
        textTransform: "uppercase",
        letterSpacing: "0.12em",
        color: "text.secondary",
        whiteSpace: "nowrap",
      }}
    >
      {title}
    </DSTypography>
    <DSBox
      sx={{
        flexGrow: 1,
        height: "1px",
        backgroundColor: "divider",
      }}
    />
  </DSBox>
);

/** A monthly collection-purchases bar chart card (one metric per card). */
const CollectionPurchasesCard = ({
  title,
  data,
  isLoading,
  dataKey,
  barName,
  format,
}: {
  title: string;
  data: CollectionPurchaseStatsResponse | undefined;
  isLoading: boolean;
  dataKey: "revenuePerClock" | "totalPurchases";
  barName: string;
  format: (value: number) => string;
}) => {
  const theme = useTheme();
  return (
    <DSCard sx={flatCardSx}>
      <DSBox sx={{ px: 2, pt: 2 }}>
        <DSTypography variant="h6">{title}</DSTypography>
      </DSBox>
      <DSCardContent>
        {isLoading ? (
          <DSBox sx={{ textAlign: "center", p: "20px" }}>
            <DSCircularProgress />
          </DSBox>
        ) : data?.length ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data} margin={{ left: 20 }}>
              <XAxis
                dataKey="month"
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                stroke={theme.palette.white[24]}
              />
              <YAxis
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                stroke={theme.palette.white[24]}
                tickFormatter={format}
                width={80}
              />
              <Tooltip
                formatter={(value: any) => format(parseFloat(String(value)))}
                contentStyle={{
                  backgroundColor: theme.palette.paper.elevation8,
                  border: "none",
                  borderRadius: 8,
                }}
                labelStyle={{ color: theme.palette.text.primary }}
                itemStyle={{ color: theme.palette.text.primary }}
                cursor={{ fill: theme.palette.white[12] }}
              />
              <Bar
                dataKey={dataKey}
                name={barName}
                fill={theme.palette.primary.main}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <DSBox
            sx={{ textAlign: "center", p: "20px", color: "text.secondary" }}
          >
            No data available
          </DSBox>
        )}
      </DSCardContent>
    </DSCard>
  );
};

/** Shared NavLink wiring (table-link localStorage behavior preserved). */
const KpiLink = ({
  tableLinked,
  link,
  children,
}: {
  tableLinked?: { tableId: string; searchValue?: string };
  link?: string;
  children: React.ReactNode;
}) =>
  tableLinked ? (
    <NavLink
      to={"/admin/" + tableLinked.tableId}
      onClick={() => {
        if (tableLinked.searchValue) {
          localStorage.setItem(
            tableLinked.tableId + "search",
            tableLinked.searchValue,
          );
        }
      }}
    >
      {children}
    </NavLink>
  ) : link ? (
    <NavLink to={link}>{children}</NavLink>
  ) : (
    <>{children}</>
  );

/** Large statement card for the hero band. */
const HeroKpi = ({
  headline,
  icon,
  value,
  secondary,
  foot,
  tableLinked,
  link,
}: {
  headline: string | JSX.Element;
  icon?: React.ReactNode;
  value: React.ReactNode;
  secondary?: React.ReactNode;
  foot?: string | JSX.Element;
  tableLinked?: { tableId: string; searchValue?: string };
  link?: string;
}) => (
  <DSCard sx={heroCardSx}>
    {icon && (
      <DSBox
        sx={{
          position: "absolute",
          top: -18,
          right: -14,
          color: "surface.accentText",
          opacity: 0.2,
          pointerEvents: "none",
          "& svg": { fontSize: "112px" },
        }}
      >
        {icon}
      </DSBox>
    )}
    <DSCardContent sx={{ flex: 1, position: "relative", p: 2.5 }}>
      <DSBox
        sx={{ typography: "body2", color: "text.secondary", mb: 0.75, pr: 7 }}
      >
        {headline}
      </DSBox>
      <DSBox
        sx={{
          fontFamily: "Gilroy-Bold",
          fontSize: { xxs: "30px", md: "38px" },
          lineHeight: 1.15,
          color: "text.primary",
          "& a": { color: "inherit", textDecoration: "none" },
        }}
      >
        <KpiLink tableLinked={tableLinked} link={link}>
          {value}
        </KpiLink>
      </DSBox>
      {secondary && (
        <DSBox sx={{ typography: "body2", color: "text.secondary", mt: 0.5 }}>
          {secondary}
        </DSBox>
      )}
    </DSCardContent>
    {foot && <DSBox sx={cardFooterSx}>{foot}</DSBox>}
  </DSCard>
);

/** Regular flat KPI card: small label on top, value below. */
const DashboardKpi = ({
  headline,
  icon,
  iconColor,
  iconImg,
  details,
  foot,
  tableLinked,
  link,
  invertColor,
}: {
  headline: string | JSX.Element;
  icon?: React.ReactNode;
  iconColor?: string;
  iconImg?: string;
  details?: React.JSX.Element | number | string;
  foot?: string | JSX.Element;
  tableLinked?: { tableId: string; searchValue?: string };
  link?: string;
  invertColor?: boolean;
}) => (
  <DSCard sx={flatCardSx}>
    <DSCardContent sx={{ flex: 1 }}>
      <DSBox
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          mb: 1,
        }}
      >
        <DSBox
          sx={{ typography: "body2", color: "text.secondary", minWidth: 0 }}
        >
          {headline}
        </DSBox>
        {iconImg ? (
          <img
            src={iconImg}
            alt="icon"
            style={{
              width: "28px",
              height: "28px",
              flexShrink: 0,
              ...(invertColor && { filter: `invert(1)` }),
            }}
          />
        ) : icon ? (
          <DSBox
            sx={{
              color: iconColor ?? "surface.accentText",
              "& svg": { fontSize: "1.5rem" },
              display: "flex",
              flexShrink: 0,
            }}
          >
            {icon}
          </DSBox>
        ) : null}
      </DSBox>
      <DSBox sx={{ typography: "h6", fontFamily: "Gilroy-Bold" }}>
        <KpiLink tableLinked={tableLinked} link={link}>
          {details}
        </KpiLink>
      </DSBox>
    </DSCardContent>
    {foot && <DSBox sx={cardFooterSx}>{foot}</DSBox>}
  </DSCard>
);

const SETTING_LABELS: Record<string, string> = {
  mode: "Mode",
  nightMode: "Night Mode",
  chimes: "Chimes",
  chimeMode: "Chime Mode",
  defaultChimeUsage: "Default Chime Usage",
  ticks: "Ticks",
  showDials: "Show Dials",
  quietTimes: "Quiet Times",
  klydosSound: "Klydos Sound",
  freshHold: "Fresh Hold",
  stayInFavorits: "Stay In Favorites",
  feedCycleMode: "Feed Cycle",
  collectionCycleMode: "Collection Cycle",
  favoritesCycleMode: "Favorites Cycle",
  showPSF: "Show PSF",
  show13Plus: "Show 13+",
};

const BAR_COLORS = [
  "#4CAF50",
  "#2196F3",
  "#FF9800",
  "#9C27B0",
  "#F44336",
  "#00BCD4",
  "#795548",
  "#607D8B",
];

function formatViewingTime(seconds: number): string {
  const totalHours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (totalHours >= 8760) {
    const years = Math.floor(totalHours / 8760);
    const remainingDays = Math.floor((totalHours % 8760) / 24);
    return `${years.toLocaleString()}y ${remainingDays}d`;
  }
  if (totalHours >= 24) {
    const days = Math.floor(totalHours / 24);
    const remainingHours = totalHours % 24;
    return `${days.toLocaleString()}d ${remainingHours}h`;
  }
  if (totalHours > 0) return `${totalHours}h ${minutes}m`;
  return `${minutes}m`;
}

const USD = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

/** Money per clock lands in cents, so it always carries two decimals. */
function formatUsd(value: number): string {
  return USD.format(value);
}

// Klydo thumbnail (video/image/placeholder) used in Klydo Stats lists
const klydoThumbStyle: React.CSSProperties = {
  width: "36px",
  height: "36px",
  borderRadius: "50%",
  objectFit: "cover",
};

function Dashboard() {
  const theme = useTheme();
  const frames: {
    [key: string]: number;
  } & { month: number; week: number; day: number } = {
    month: 60 * 60 * 24 * 30.5 * 1000,
    week: 60 * 60 * 24 * 7 * 1000,
    day: 60 * 60 * 24 * 1000,
  };
  const [kpis, setKpis] = useState<Kpi>();
  const [newRegisteres, setNewRegisteres] = useState<number>(0);
  const [timeWindow, setTimeWindow] = useState<{ key: string; value: number }>({
    key: localStorage.getItem("timeWindow") || "month",
    value: frames[localStorage.getItem("timeWindow") || "month"],
  });
  const { data: monthlyRegisteredDevices } = useMonthlyRegisteredDevices();
  const { data: artistsClocksCount } = useArtistsClocksCount();
  const { data: artistsCreatedKlydos } = useNumberOfAuthorsCreatedKlydos();
  const { data: pendingPublicationRequests } =
    useNumberOfPendingPublicationRequests();
  const { data: collectionsInReview } = useNumberOfCollectionsInReview();
  const { data: collectionsInMarketplace } =
    useNumberOfCollectionsInMarketplace();
  const { data: unregisteredDevicesCount } = useUnregisteredDevicesCount();
  const { data: openedSwissClocks } = useOpenedSwissClocksCount();
  const { data: klydoStats, isLoading: klydoStatsLoading } = useKlydoStats();
  const { data: collectionAnalytics, isLoading: collectionAnalyticsLoading } =
    useCollectionAnalytics();
  const {
    data: collectionPurchaseStats,
    isLoading: collectionPurchaseStatsLoading,
  } = useCollectionPurchaseStats();
  const [exploreDays, setExploreDays] = useState<number>(1);
  const { data: exploreClocksCount } = useExploreClocksCount(exploreDays);
  const [artistsLandingDays, setArtistsLandingDays] = useState<number>(7);
  const { data: artistsLandingStats, isLoading: artistsLandingLoading } =
    useArtistsLandingPageStats(artistsLandingDays);
  const [settingsFromVersion, setSettingsFromVersion] = useState<number>(600);
  const { data: settingsDistribution, isLoading: settingsLoading } =
    useClockSettingsDistribution(settingsFromVersion);
  const { data: collabpayWidgetData } = useCollabpayWidgetData();
  const [showUnregisteredDevicesModal, setShowUnregisteredDevicesModal] =
    useState(false);
  const { data: dashboardOrders } = useDashboardOrders();
  const orderStats = useMemo(() => {
    if (!dashboardOrders) return undefined;
    const now = Date.now();
    const count = (from: number, to: number) => {
      let total = 0;
      let clocks = 0;
      let collections = 0;
      dashboardOrders.forEach((order) => {
        const time = order.date.getTime();
        if (time > from && time <= to) {
          total++;
          if (order.isClockOrder) clocks++;
          if (order.isCollectionOrder) collections++;
        }
      });
      return { total, clocks, collections };
    };
    const current = count(now - timeWindow.value, now);
    const previous = count(now - timeWindow.value * 2, now - timeWindow.value);
    const difference =
      current.total === 0 && previous.total === 0
        ? 0
        : Math.round(
            (current.total * 100) /
              (previous.total > 0 ? previous.total : Number.MIN_VALUE) -
              100,
          );
    return { current, previous, difference };
  }, [dashboardOrders, timeWindow]);
  useEffect(() => {
    let prevListen: Kpi | undefined = undefined;

    const lKpis = (ks: Kpi) => {
      if (prevListen) {
        const newRegis = ks!.activeDevices! - prevListen.activeDevices!;
        setNewRegisteres(newRegis);
      }
      prevListen = ks;
      setKpis(ks);
    };

    firebaseService.listenKpis(lKpis);

    return () => {
      firebaseService.stopListenKpis(lKpis);
    };
  }, []);

  return (
    <>
      <DSBox sx={{ width: "100%", p: { xxs: 2, md: 3 } }}>
        <PageHeader
          overline="Overview"
          title="Dashboard"
          subtitle="Operations at a glance"
        />

        {/* Hero band — the four headline numbers */}
        <DSBox
          sx={{
            display: "grid",
            gap: 2,
            alignItems: "stretch",
            gridTemplateColumns: {
              xxs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            },
          }}
        >
          <HeroKpi
            headline="Active Devices"
            icon={<InsightsOutlinedIcon />}
            value={kpis?.activeDevices || <DSCircularProgress size={20} />}
            tableLinked={{ tableId: "devices", searchValue: "" }}
            foot={
              <div>
                {/* {kpis?.lastDayRegistered ? `${kpis?.lastDayRegistered} registered (last 24 hrs)` : <></>}<br /> */}
                {newRegisteres > 0 && (
                  <DSBox sx={{ display: "flex", alignItems: "center" }}>
                    <FaArrowUp style={{ color: "#4CAF50" }} /> &nbsp;+
                    {newRegisteres} last 15 mins
                  </DSBox>
                )}
              </div>
            }
          />
          <HeroKpi
            headline="Live Devices"
            icon={<InsightsOutlinedIcon />}
            value={kpis ? kpis.liveDevices : <DSCircularProgress size={20} />}
            secondary={
              kpis ? <>Last 24hr {kpis.connectedDevices}</> : undefined
            }
            foot={
              <>
                <i className="far fa-calendar-alt" style={{ marginRight: 4 }} />
                last 10 minutes
              </>
            }
            tableLinked={{
              tableId: "devices",
              searchValue: `heartbeat>${new Date().getTime() - 600000}//config`,
            }}
          />
          <HeroKpi
            headline="Orders"
            icon={<ShoppingCartOutlinedIcon />}
            value={
              orderStats ? (
                orderStats.current.total
              ) : (
                <DSCircularProgress size={20} />
              )
            }
            tableLinked={{ tableId: "orders" }}
            secondary={
              orderStats ? (
                <>
                  Clocks {orderStats.current.clocks} &middot; Collections{" "}
                  {orderStats.current.collections} &middot; last{" "}
                  {timeWindow.key}: {orderStats.previous.total}
                </>
              ) : undefined
            }
            foot={
              <DSBox
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {orderStats &&
                  orderStats.difference !== 0 &&
                  Number.isFinite(orderStats.difference) && (
                    <DSTypography
                      sx={{
                        fontSize: 12,
                        m: 0,
                        color:
                          orderStats.difference > 0 ? "#4CAF50" : "#F44336",
                      }}
                    >
                      {Math.abs(orderStats.difference)}%
                      {orderStats.difference > 0 ? (
                        <FaArrowUp
                          style={{
                            color: "#4CAF50",
                            transform: "rotate(-35deg)",
                          }}
                        />
                      ) : (
                        <FaArrowDown
                          style={{
                            color: "#F44336",
                            transform: "rotate(-35deg)",
                          }}
                        />
                      )}
                    </DSTypography>
                  )}
                <DSBox sx={{ width: 0, m: 0 }} />
                <DSSelect<string>
                  size="small"
                  defaultValue={timeWindow.key}
                  onChange={(v) => {
                    const value = v.target.value;
                    setTimeWindow({ key: value, value: frames[value] });
                    localStorage.setItem("timeWindow", value);
                  }}
                  sx={{ fontSize: 11 }}
                >
                  <DSMenuItem value="month">month</DSMenuItem>
                  <DSMenuItem value="week">week</DSMenuItem>
                  <DSMenuItem value="day">day</DSMenuItem>
                </DSSelect>
              </DSBox>
            }
          />
          <HeroKpi
            headline="Versions"
            icon={<LightbulbOutlinedIcon />}
            value={
              firebaseService.general ? (
                firebaseService.general.public
              ) : (
                <DSCircularProgress size={20} />
              )
            }
            secondary={
              firebaseService.general ? (
                <>
                  public &middot; beta {firebaseService.general.beta} &middot;
                  last {firebaseService.general.last}
                </>
              ) : undefined
            }
          />
        </DSBox>

        {/* ------------------------------------------------------------- */}
        <SectionHeader title="Fleet" />
        <DSBox sx={sectionGridSx}>
          <DSCard sx={flatCardSx}>
            <DSBox
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1,
                px: 2,
                pt: 2,
              }}
            >
              <DSBox sx={{ typography: "body2", color: "text.secondary" }}>
                Opened Swiss clocks
              </DSBox>
              <DSBox
                sx={{
                  color: "#4CAF50",
                  "& svg": { fontSize: "1.5rem" },
                  display: "flex",
                  flexShrink: 0,
                }}
              >
                <AccessAlarmOutlinedIcon />
              </DSBox>
            </DSBox>
            <DSBox
              sx={{
                px: 2,
                pt: 0.5,
                typography: "h6",
                fontFamily: "Gilroy-Bold",
              }}
            >
              {openedSwissClocks ? (
                `${openedSwissClocks.opened} / ${openedSwissClocks.total}`
              ) : (
                <DSCircularProgress size={20} />
              )}
            </DSBox>
            <DSCardContent
              sx={{ flex: 1, maxHeight: 300, overflowY: "auto", pt: 1 }}
            >
              {!openedSwissClocks ? null : openedSwissClocks.clocks.length ===
                0 ? (
                <DSBox sx={{ color: "text.secondary", fontSize: 13 }}>
                  No opened clocks yet
                </DSBox>
              ) : (
                <DSTable size="small" sx={{ width: "100%" }}>
                  <DSTableHead>
                    <DSTableRow>
                      <DSTableCell>Device ID</DSTableCell>
                      <DSTableCell>Registered</DSTableCell>
                    </DSTableRow>
                  </DSTableHead>
                  <DSTableBody>
                    {openedSwissClocks.clocks.map((c) => (
                      <DSTableRow
                        key={c.idf}
                        hover
                        sx={{
                          cursor: "pointer",
                          "& a": {
                            color: "inherit",
                            textDecoration: "none",
                          },
                        }}
                      >
                        <DSTableCell>
                          <NavLink
                            to={`/admin/devices?query=${encodeURIComponent(
                              c.idf,
                            )}`}
                            title={c.idf}
                          >
                            {c.idf}
                          </NavLink>
                        </DSTableCell>
                        <DSTableCell>
                          <NavLink
                            to={`/admin/devices?query=${encodeURIComponent(
                              c.idf,
                            )}`}
                          >
                            {c.registeredAt
                              ? c.registeredAt.toLocaleDateString(undefined, {
                                  year: "numeric",
                                  month: "numeric",
                                  day: "numeric",
                                })
                              : "-"}
                          </NavLink>
                        </DSTableCell>
                      </DSTableRow>
                    ))}
                  </DSTableBody>
                </DSTable>
              )}
            </DSCardContent>
          </DSCard>
          <DSCard sx={flatCardSx}>
            <DSBox sx={{ px: 2, pt: 2 }}>
              <DSTypography variant="body2" sx={{ color: "text.secondary" }}>
                Registered per FW per Month
              </DSTypography>
            </DSBox>
            <DSCardContent sx={{ flex: 1, maxHeight: 360, overflowY: "auto" }}>
              {Array.isArray(monthlyRegisteredDevices) ? (
                <DSTable size="small" sx={{ width: "100%" }}>
                  <DSTableHead>
                    <DSTableRow>
                      <DSTableCell>Firmware Version</DSTableCell>
                      <DSTableCell>Month</DSTableCell>
                      <DSTableCell>Amount</DSTableCell>
                    </DSTableRow>
                  </DSTableHead>
                  <DSTableBody>
                    {monthlyRegisteredDevices.map((item, index) => (
                      <DSTableRow key={index}>
                        <DSTableCell>{item.firmware_version}</DSTableCell>
                        <DSTableCell>{item.month_registered}</DSTableCell>
                        <DSTableCell>{item.amount}</DSTableCell>
                      </DSTableRow>
                    ))}
                  </DSTableBody>
                </DSTable>
              ) : (
                <DSCircularProgress size={20} />
              )}
            </DSCardContent>
          </DSCard>
          <DSCard sx={flatCardSx}>
            <DSCardContent sx={{ flex: 1 }}>
              <DSBox
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 1,
                }}
              >
                <DSTypography variant="body2" sx={{ color: "text.secondary" }}>
                  New Version Onboarding Status
                </DSTypography>
                <img
                  src={klydoErrorIcon}
                  alt="icon"
                  style={{
                    width: "28px",
                    height: "28px",
                    flexShrink: 0,
                    filter: "invert(1)",
                  }}
                />
              </DSBox>
              {unregisteredDevicesCount == null ? (
                <DSCircularProgress size={20} />
              ) : (
                <DSBox sx={{ fontSize: 14 }}>
                  <DSBox
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowUnregisteredDevicesModal(true)}
                  >
                    <span style={{ color: "#F44336" }}>Unregistered:</span>
                    <DSBox sx={{ typography: "h6", fontFamily: "Gilroy-Bold" }}>
                      {unregisteredDevicesCount ?? "..."}
                    </DSBox>
                  </DSBox>
                </DSBox>
              )}
            </DSCardContent>
            <DSBox sx={cardFooterSx}>V&ge;600, Active 2d</DSBox>
          </DSCard>
          <RemindersWidget />
          <AvgUniqueKlydosWidget />
          <DailyRhythmCollectionWidget />
          <DSBox sx={wideSpanSx}>
            <ReminderKlydosListWidget />
          </DSBox>
          <EcoModeWidget />
          <DSCard sx={{ ...flatCardSx, ...fullSpanSx }}>
            <DSBox
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                pt: 2,
                gap: 1,
              }}
            >
              <DSTypography variant="h6">
                Clock Settings Distribution
              </DSTypography>
              <DSStack direction="row" alignItems="center" spacing={1}>
                <DSTypography
                  variant="caption"
                  sx={{ color: "text.secondary" }}
                >
                  V&ge;
                </DSTypography>
                <DSTextField
                  type="number"
                  size="small"
                  value={settingsFromVersion}
                  onChange={(e) =>
                    setSettingsFromVersion(Number(e.target.value) || 0)
                  }
                  sx={{ width: 90 }}
                />
              </DSStack>
            </DSBox>
            <DSCardContent>
              {settingsLoading ? (
                <DSBox sx={{ textAlign: "center", p: "20px" }}>
                  <DSCircularProgress />
                </DSBox>
              ) : settingsDistribution ? (
                <DSBox
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: {
                      xxs: "repeat(2, 1fr)",
                      md: "repeat(3, 1fr)",
                      lg: "repeat(4, 1fr)",
                    },
                  }}
                >
                  {Object.entries(settingsDistribution).map(
                    ([setting, values]) => {
                      const total = values.reduce((sum, v) => sum + v.count, 0);
                      const filtered = values.filter(
                        (v) => total > 0 && v.count > 0,
                      );
                      return (
                        <DSBox key={setting} sx={{ mb: 2 }}>
                          <DSTypography
                            variant="subtitle2"
                            sx={{ mb: "6px", fontSize: 13 }}
                          >
                            {SETTING_LABELS[setting] || setting}
                          </DSTypography>
                          {filtered.map((v, i) => {
                            const pct = Math.round((v.count / total) * 100);
                            const pctLabel = pct === 0 ? "<1%" : `${pct}%`;
                            return (
                              <DSBox key={v.value} sx={{ mb: "3px" }}>
                                <DSBox
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    fontSize: 11,
                                    mb: "1px",
                                  }}
                                >
                                  <span>{v.value}</span>
                                  <span
                                    style={{
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    {v.count} ({pctLabel})
                                  </span>
                                </DSBox>
                                <DSBox
                                  sx={{
                                    height: "6px",
                                    backgroundColor: "white.12",
                                    borderRadius: "3px",
                                    overflow: "hidden",
                                  }}
                                >
                                  <DSBox
                                    sx={{
                                      height: "100%",
                                      width: `${Math.max(pct, 1)}%`,
                                      backgroundColor:
                                        BAR_COLORS[i % BAR_COLORS.length],
                                      borderRadius: "3px",
                                    }}
                                  />
                                </DSBox>
                              </DSBox>
                            );
                          })}
                        </DSBox>
                      );
                    },
                  )}
                </DSBox>
              ) : null}
            </DSCardContent>
          </DSCard>
          <DSBox sx={fullSpanSx}>
            <MenuNavigationWidget />
          </DSBox>
          <HeartLongPressWidget />
        </DSBox>

        {/* ------------------------------------------------------------- */}
        <SectionHeader title="Studio & Artists" />
        <DSBox sx={sectionGridSx}>
          <DSBox sx={wideSpanSx}>
            <AiGreetAnalyticsWidget />
          </DSBox>
          <DSBox sx={wideSpanSx}>
            <KlydoGreetOrdersWidget />
          </DSBox>
          <DSBox sx={wideSpanSx}>
            <StudioUsersWidget />
          </DSBox>
          <DSBox sx={wideSpanSx}>
            <CollectionProductPageEngagementWidget />
          </DSBox>
          <DSBox sx={wideSpanSx}>
            <ArtistPreviewedKlydoWidget />
          </DSBox>
          <DSBox sx={wideSpanSx}>
            <SentToClockWidget />
          </DSBox>
          <DSStack spacing={2} sx={{ height: "100%" }}>
            <DashboardKpi
              headline="Number of authors created klydos"
              icon={<PaletteOutlinedIcon />}
              iconColor="#2196F3"
              details={artistsCreatedKlydos || <DSCircularProgress size={20} />}
            />
            <DashboardKpi
              headline="Artists clocks"
              icon={<AccessAlarmOutlinedIcon />}
              iconColor="#4CAF50"
              details={artistsClocksCount ?? <DSCircularProgress size={20} />}
              link="/admin/devices?artist=ONLY&dev=EXCLUDE"
            />
          </DSStack>
          <DSCard sx={{ ...flatCardSx, ...wideSpanSx }}>
            <DSBox
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                pt: 2,
                gap: 1,
              }}
            >
              <DSTypography variant="h6">
                Artists Landing Page Behavior
              </DSTypography>
              <DSStack direction="row" alignItems="center" spacing={1}>
                <DSTypography
                  variant="caption"
                  sx={{ color: "text.secondary" }}
                >
                  Last
                </DSTypography>
                <DSSelect<number>
                  size="small"
                  value={artistsLandingDays}
                  onChange={(e) =>
                    setArtistsLandingDays(Number(e.target.value))
                  }
                  sx={{ fontSize: 12 }}
                >
                  {[1, 7, 14, 30, 60, 90].map((d) => (
                    <DSMenuItem key={d} value={d}>
                      {d}d
                    </DSMenuItem>
                  ))}
                </DSSelect>
              </DSStack>
            </DSBox>
            <DSCardContent
              sx={{
                maxHeight: "520px",
                overflowY: "auto",
                pb: "20px",
              }}
            >
              {artistsLandingLoading ? (
                <DSBox sx={{ textAlign: "center", p: "20px" }}>
                  <DSCircularProgress />
                </DSBox>
              ) : artistsLandingStats &&
                artistsLandingStats.totalBecomeArtistClicks > 0 ? (
                <>
                  <DSBox
                    sx={{
                      fontSize: 13,
                      mb: 1,
                      color: "text.secondary",
                    }}
                  >
                    Of{" "}
                    <strong>
                      {artistsLandingStats.totalBecomeArtistClicks.toLocaleString()}
                    </strong>{" "}
                    sessions that clicked{" "}
                    <strong>&quot;become_artist&quot;</strong>:
                  </DSBox>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={[
                          {
                            name: "Google Sign In",
                            value: artistsLandingStats.googleSignInClicks,
                            percentage:
                              artistsLandingStats.googleSignInPercentage,
                          },
                          {
                            name: "Join Now",
                            value: artistsLandingStats.joinNowClicks,
                            percentage: artistsLandingStats.joinNowPercentage,
                          },
                          {
                            name: "No Follow-up",
                            value: artistsLandingStats.noFollowUpClicks,
                            percentage:
                              artistsLandingStats.noFollowUpPercentage,
                          },
                        ]}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        label={(entry: any) => `${entry.percentage}%`}
                      >
                        <Cell fill="#4285F4" />
                        <Cell fill="#34A853" />
                        <Cell fill="#9E9E9E" />
                      </Pie>
                      <Tooltip
                        formatter={(value: any, name: any, props: any) => [
                          `${value.toLocaleString()} (${props.payload.percentage}%)`,
                          name,
                        ]}
                        contentStyle={{
                          backgroundColor: theme.palette.paper.elevation8,
                          border: "none",
                          borderRadius: 8,
                        }}
                      />
                      <Legend
                        wrapperStyle={{ color: theme.palette.text.secondary }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  {(
                    [
                      {
                        label: "Onboarding started",
                        value: artistsLandingStats.onboardingStarted,
                      },
                      {
                        label: `Onboarding skipped${artistsLandingStats.avgOnboardingSkipSlide != null ? ` (avg slide ${artistsLandingStats.avgOnboardingSkipSlide})` : ""}`,
                        value: artistsLandingStats.onboardingSkipped,
                      },
                      {
                        label: "Onboarding completed",
                        value: artistsLandingStats.onboardingCompleted,
                      },
                      {
                        label: "Studio contract signed",
                        value: artistsLandingStats.studioContractSigned,
                      },
                      {
                        label: "Dashboard first visit",
                        value: artistsLandingStats.dashboardFirstVisit,
                      },
                      {
                        label: "Klydo upload started",
                        value: artistsLandingStats.klydoUploadStarted,
                      },
                      {
                        label: "Klydo published",
                        value: artistsLandingStats.klydoPublished,
                      },
                      {
                        label: "Collection created",
                        value: artistsLandingStats.collectionCreated,
                      },
                      {
                        label: "Collection published",
                        value: artistsLandingStats.collectionPublished,
                      },
                    ] as { label: string; value: number }[]
                  ).map(({ label, value }) => (
                    <DSBox
                      key={label}
                      sx={{
                        mt: "6px",
                        px: "12px",
                        py: 1,
                        backgroundColor: "surface.border",
                        borderRadius: "6px",
                        fontSize: 13,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ color: theme.palette.text.secondary }}>
                        {label}
                      </span>
                      <strong>{(value ?? 0).toLocaleString()}</strong>
                    </DSBox>
                  ))}
                </>
              ) : (
                <DSBox
                  sx={{
                    textAlign: "center",
                    p: "20px",
                    color: "text.secondary",
                  }}
                >
                  No data available
                </DSBox>
              )}
            </DSCardContent>
          </DSCard>
        </DSBox>

        {/* ------------------------------------------------------------- */}
        <SectionHeader title="Content" />
        <DSBox sx={sectionGridSx}>
          <DSCard sx={{ ...flatCardSx, ...wideSpanSx }}>
            <DSBox sx={{ px: 2, pt: 2 }}>
              <DSTypography variant="h6">Klydo Stats</DSTypography>
            </DSBox>
            <DSCardContent>
              {klydoStatsLoading ? (
                <DSBox sx={{ textAlign: "center", p: "20px" }}>
                  <DSCircularProgress />
                </DSBox>
              ) : (
                <>
                  <DSBox
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xxs: "1fr",
                        sm: "repeat(3, 1fr)",
                      },
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <DSBox sx={{ fontSize: 14 }}>
                      <strong>Total Favorites:</strong>{" "}
                      {klydoStats?.totalFavorites?.toLocaleString() ?? 0}
                    </DSBox>
                    <DSBox sx={{ fontSize: 14 }}>
                      <strong>Total Viewing Time:</strong>{" "}
                      {formatViewingTime(klydoStats?.totalViewingTime ?? 0)}
                    </DSBox>
                  </DSBox>
                  <DSBox
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xxs: "1fr",
                        sm: "repeat(3, 1fr)",
                      },
                      gap: 1,
                    }}
                  >
                    <DSBox>
                      <DSTypography variant="subtitle2" sx={{ mb: 1 }}>
                        Top Favorited
                      </DSTypography>
                      {klydoStats?.topFavoritedKlydos.map((k) => (
                        <DSBox
                          key={k.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 1,
                            gap: 1,
                          }}
                        >
                          {isVideo(k.loopUrl) === "video" ? (
                            <video
                              src={resizeCloudinary(k.loopUrl, 48)?.replace(
                                "c_scale",
                                "c_scale,r_max",
                              )}
                              muted
                              autoPlay
                              loop
                              playsInline
                              style={klydoThumbStyle}
                            />
                          ) : (
                            <img
                              src={resizeCloudinary(k.loopUrl, 48)?.replace(
                                "c_scale",
                                "c_scale,r_max",
                              )}
                              alt={k.name}
                              style={klydoThumbStyle}
                            />
                          )}
                          <DSBox
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              fontSize: 13,
                            }}
                          >
                            <DSBox
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {k.name}
                            </DSBox>
                            <DSBox sx={{ color: "text.secondary" }}>
                              {k.favorites.toLocaleString()} favorites
                            </DSBox>
                          </DSBox>
                        </DSBox>
                      ))}
                    </DSBox>
                    <DSBox>
                      <DSTypography variant="subtitle2" sx={{ mb: 1 }}>
                        Top Viewed
                      </DSTypography>
                      {klydoStats?.topViewedKlydos.map((k) => (
                        <DSBox
                          key={k.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 1,
                            gap: 1,
                          }}
                        >
                          {isVideo(k.loopUrl) === "video" ? (
                            <video
                              src={resizeCloudinary(k.loopUrl, 48)?.replace(
                                "c_scale",
                                "c_scale,r_max",
                              )}
                              muted
                              autoPlay
                              loop
                              playsInline
                              style={klydoThumbStyle}
                            />
                          ) : (
                            <img
                              src={resizeCloudinary(k.loopUrl, 48)?.replace(
                                "c_scale",
                                "c_scale,r_max",
                              )}
                              alt={k.name}
                              style={klydoThumbStyle}
                            />
                          )}
                          <DSBox
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              fontSize: 13,
                            }}
                          >
                            <DSBox
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {k.name}
                            </DSBox>
                            <DSBox sx={{ color: "text.secondary" }}>
                              {k.viewed.toLocaleString()} views
                            </DSBox>
                          </DSBox>
                        </DSBox>
                      ))}
                    </DSBox>
                    <DSBox>
                      <DSTypography variant="subtitle2" sx={{ mb: 1 }}>
                        Top Deleted
                      </DSTypography>
                      {klydoStats?.topDeletedKlydos.map((k) => (
                        <DSBox
                          key={k.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 1,
                            gap: 1,
                          }}
                        >
                          {k.loopUrl ? (
                            isVideo(k.loopUrl) === "video" ? (
                              <video
                                src={resizeCloudinary(k.loopUrl, 48)?.replace(
                                  "c_scale",
                                  "c_scale,r_max",
                                )}
                                muted
                                autoPlay
                                loop
                                playsInline
                                style={klydoThumbStyle}
                              />
                            ) : (
                              <img
                                src={resizeCloudinary(k.loopUrl, 48)?.replace(
                                  "c_scale",
                                  "c_scale,r_max",
                                )}
                                alt={k.name}
                                style={klydoThumbStyle}
                              />
                            )
                          ) : (
                            <DSBox
                              sx={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "50%",
                                backgroundColor: "white.12",
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <DSBox
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              fontSize: 13,
                            }}
                          >
                            <DSBox
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {k.name}
                            </DSBox>
                            <DSBox sx={{ color: "text.secondary" }}>
                              {k.deleted.toLocaleString()} deletions
                            </DSBox>
                          </DSBox>
                        </DSBox>
                      ))}
                    </DSBox>
                  </DSBox>
                </>
              )}
            </DSCardContent>
          </DSCard>
          <NewClockOSFeaturesCard />
          <DashboardKpi
            headline="Publication requests pending"
            icon={<CollectionsOutlinedIcon />}
            iconColor="#FF9800"
            details={
              pendingPublicationRequests ?? <DSCircularProgress size={20} />
            }
            link={`/admin/reviews?tab=${ReviewTab.Klydos}`}
          />
          <DashboardKpi
            headline="Collections in review"
            icon={<FormatListBulletedOutlinedIcon />}
            iconColor="#2196F3"
            details={collectionsInReview ?? <DSCircularProgress size={20} />}
            link={`/admin/reviews?tab=${ReviewTab.Collections}`}
          />
          <DashboardKpi
            headline="Collections in marketplace"
            icon={<FormatListBulletedOutlinedIcon />}
            iconColor="#2196F3"
            details={
              collectionsInMarketplace ?? <DSCircularProgress size={20} />
            }
            link={`/admin/collections?marketplace=true`}
          />
          <DashboardKpi
            headline="Explore Feature Usage"
            icon={<SearchOutlinedIcon />}
            iconColor="#4CAF50"
            details={
              exploreClocksCount?.numberOfClocks?.toLocaleString() ?? (
                <DSCircularProgress size={20} />
              )
            }
            foot={
              <DSBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <span>Past</span>
                <DSTextField
                  type="number"
                  size="small"
                  inputProps={{ min: 1 }}
                  value={exploreDays}
                  onChange={(e) =>
                    setExploreDays(Math.max(1, Number(e.target.value) || 1))
                  }
                  sx={{ width: 70 }}
                />
                <span>day(s)</span>
              </DSBox>
            }
          />
          <DSBox sx={wideSpanSx}>
            <KlydoFeedAnalyticsCard />
          </DSBox>
          <DSBox sx={wideSpanSx}>
            <ExploreFavoritesCard />
          </DSBox>
          <DSBox sx={wideSpanSx}>
            <AddCollectionQrCard />
          </DSBox>
        </DSBox>

        {/* ------------------------------------------------------------- */}
        <SectionHeader title="Commerce" />
        <DSBox sx={sectionGridSx}>
          <DSCard sx={{ ...flatCardSx, ...wideSpanSx }}>
            <DSBox sx={{ px: 2, pt: 2 }}>
              <DSTypography variant="h6">Collection Analytics</DSTypography>
            </DSBox>
            <DSCardContent>
              {collectionAnalyticsLoading ? (
                <DSBox sx={{ textAlign: "center", p: "20px" }}>
                  <DSCircularProgress />
                </DSBox>
              ) : collectionAnalytics ? (
                <>
                  <DSBox
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xxs: "1fr",
                        sm: "repeat(3, 1fr)",
                      },
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <DSBox sx={{ fontSize: 14 }}>
                      <strong>Clocks with Collections:</strong>{" "}
                      {collectionAnalytics.devicesWithCollections.toLocaleString()}{" "}
                      / {collectionAnalytics.totalDevices.toLocaleString()}
                    </DSBox>
                    <DSBox sx={{ fontSize: 14 }}>
                      <strong>Adoption Rate:</strong>{" "}
                      {collectionAnalytics.percentage}%
                    </DSBox>
                    <DSBox sx={{ fontSize: 14 }}>
                      <strong>First Collection (24h):</strong>{" "}
                      {collectionAnalytics.firstCollectionLast24h.toLocaleString()}
                    </DSBox>
                  </DSBox>
                  <DSTypography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontSize: 13,
                      color: "text.secondary",
                    }}
                  >
                    Collections per Clock
                  </DSTypography>
                  {collectionAnalytics.distribution
                    .filter((d) => d.bucket !== "0")
                    .map((d, i) => {
                      const maxPct = Math.max(
                        ...collectionAnalytics.distribution
                          .filter((b) => b.bucket !== "0")
                          .map((b) => b.percentage),
                      );
                      const barWidth =
                        maxPct > 0 ? (d.percentage / maxPct) * 100 : 0;
                      return (
                        <DSBox key={d.bucket} sx={{ mb: "6px" }}>
                          <DSBox
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              fontSize: 12,
                              mb: "2px",
                            }}
                          >
                            <span>{d.bucket} collections</span>
                            <span
                              style={{ color: theme.palette.text.secondary }}
                            >
                              {d.count.toLocaleString()} clocks ({d.percentage}
                              %)
                            </span>
                          </DSBox>
                          <DSBox
                            sx={{
                              height: "8px",
                              backgroundColor: "white.12",
                              borderRadius: "4px",
                              overflow: "hidden",
                            }}
                          >
                            <DSBox
                              sx={{
                                height: "100%",
                                width: `${Math.max(barWidth, 1)}%`,
                                backgroundColor:
                                  BAR_COLORS[i % BAR_COLORS.length],
                                borderRadius: "4px",
                              }}
                            />
                          </DSBox>
                        </DSBox>
                      );
                    })}
                </>
              ) : null}
            </DSCardContent>
          </DSCard>
          <UpfrontPaymentsWidget />
          <AffiliatePaymentsWidget />
          <DashboardKpi
            headline="Payments setup"
            icon={<PaidOutlinedIcon />}
            iconColor="#4CAF50"
            details={
              collabpayWidgetData == null ? (
                <DSCircularProgress size={20} />
              ) : (
                <DSBox sx={{ fontSize: 14 }}>
                  <DSBox
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span style={{ color: "#4CAF50" }}>With payout:</span>
                    <strong>
                      {collabpayWidgetData.collaboratorsWithPaymentOptions}
                    </strong>
                  </DSBox>
                  <DSBox
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span style={{ color: "#F44336" }}>Missing payout:</span>
                    <strong>
                      {
                        collabpayWidgetData.collaboratorsWithoutPaymentOptionsButActive
                      }
                    </strong>
                  </DSBox>
                </DSBox>
              )
            }
            foot="Collaborators with active collections / pending payouts"
          />
          <DSBox
            sx={{
              ...fullSpanSx,
              display: "grid",
              gap: 2,
              alignItems: "stretch",
              gridTemplateColumns: { xxs: "1fr", sm: "repeat(2, 1fr)" },
            }}
          >
            <CollectionPurchasesCard
              title="Money per User per Month"
              data={collectionPurchaseStats}
              isLoading={collectionPurchaseStatsLoading}
              dataKey="revenuePerClock"
              barName="Revenue per Clock"
              format={formatUsd}
            />
            <CollectionPurchasesCard
              title="Total Collection Sales per Month"
              data={collectionPurchaseStats}
              isLoading={collectionPurchaseStatsLoading}
              dataKey="totalPurchases"
              barName="Total Sales"
              format={(value) => value.toLocaleString()}
            />
          </DSBox>
        </DSBox>
      </DSBox>
      <UnregisteredDevicesListModal
        open={showUnregisteredDevicesModal}
        onClose={() => setShowUnregisteredDevicesModal(false)}
      />
    </>
  );
}

export default Dashboard;
